import React, { useEffect, useState } from 'react';
// https://docs.videojs.com/index.html
import videojs from 'video.js';
import 'video.js/dist/video-js.css';
import 'videojs-youtube';
import Overlay from './utils/Overlay';
import FloatingInfo from './utils/FloatingInfo';
import Icon from '@ff/ui/Icon';
import { createUseStyles } from 'react-jss';

export default function VideoJS(props) {
  const { options, onReady, onClick, onClose, overlay, overlayOptions = {}, dataSetup, floatingInfo = {} } = props;
  const { autoplay } = options;

  const { src, type } = options?.sources || {};

  const c = useStyles();
  const videoRef = React.useRef(null);
  const playerRef = React.useRef(null);
  const timeoutRef = React.useRef(null);

  const [showFloatingInfo, setFloatingInfo] = useState(false);

  // init player
  useEffect(() => {
    // make sure Video.js player is only initialized once
    if (!playerRef.current) {
      const videoElement = videoRef.current;
      if (!videoElement) return;

      playerRef.current = videojs(videoElement, options, () => {
        onReady?.(playerRef.current);
      });
    }
  }, [onReady, options]);

  // change player source when src and type change, options is new object every render
  useEffect(() => {
    if (playerRef.current && src && type) {
      playerRef.current.src({
        src,
        type
      });
    }
  }, [src, type]);

  // Dispose the Video.js player when the functional component unmounts
  useEffect(() => {
    const player = playerRef.current;

    return () => {
      if (player) {
        player.dispose();
        playerRef.current = null;
      }
    };
  }, [playerRef]);

  // DDEV-3609: Enable autoplay in WeChat browser
  useEffect(() => {
    const play = () => {
      if (playerRef.current && autoplay) {
        playerRef.current.play();
      }
    };

    document.addEventListener('WeixinJSBridgeReady', play, false);
    // Add handler for Android
    document.addEventListener('click', play, { once: true });

    return () => {
      document.removeEventListener('click', play);
      document.removeEventListener('WeixinJSBridgeReady', play, false);
    };
  }, [autoplay]);

  const handleClose = (event) => {
    // video will be paused without preventDefault
    event.preventDefault();
    // prevent click propagation to video container onclick
    event.stopPropagation();
    onClose(event);
  }

  const handleShowOverlay = () => {
    if (!showFloatingInfo) {
      setFloatingInfo(true);
    }

    clearTimeout(timeoutRef.current);

    // hide overlay after some time
    timeoutRef.current = setTimeout(() => {
      setFloatingInfo(false);
    }, 5000);
  }

  return (
    <div
      data-vjs-player
      onClick={onClick}
      onTouchEnd={onClick} // for mobile
      onMouseMove={handleShowOverlay}
    >
      <video
        ref={videoRef}
        className="video-js vjs-big-play-centered"
        playsInline // need this for autoplay on iOS
        data-setup={dataSetup} // for youtube player
      />
      {onClose &&
        <Icon
          name="ff-icon-cross"
          className={c.closeButton}
          onClick={handleClose}
          onTouchEnd={handleClose} // for mobile
        />
      }
      {overlay &&
        <Overlay
          title={overlayOptions.title}
          subtitle={overlayOptions.subtitle}
          placeholderImageUrl={overlayOptions.placeholderImageUrl}
        />
      }
      {showFloatingInfo &&
        <FloatingInfo
          title={floatingInfo.title}
          subtitle={floatingInfo.subtitle}
        />
      }
    </div>
  );
}

const useStyles = createUseStyles({
  closeButton: {
    position: 'absolute',
    top: 10,
    right: 10,
    cursor: 'pointer'
  }
});
